import React, { useEffect, useState } from 'react';
import { useToast } from '../contexts/ToastContext';
import { acquireBodyScrollLock } from '../utils/bodyScrollLock';

const escapeHtml = (value: string | number | undefined | null): string => {
  if (value === undefined || value === null) {
    return '';
  }
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

interface Vehicle {
  id: number;
  year: number;
  make: string;
  model: string;
  trim: string;
  color?: string;
  exterior_finish_label?: string | null;
  nickname?: string;
  description?: string;
  display_name: string;
  horsepower?: number;
  horsepower_measurement?: 'wheel' | 'crank' | null;
  torque?: number;
  redline?: number;
}

interface Modification {
  id: string;
  title: string;
  description: string;
  category: string;
  vendor: string;
  cost?: number;
  install_date?: string;
  notes?: string;
  part_number?: string;
  specifications?: any;
  parentComponent?: string;
  power_gain?: number;
  torque_gain?: number;
  weight_change?: number;
}

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehicle: Vehicle;
  modifications: Modification[];
  exportType: 'json' | 'excel' | 'pdf' | 'text';
}

export const ExportModal: React.FC<ExportModalProps> = ({
  isOpen,
  onClose,
  vehicle,
  modifications,
  exportType
}) => {
  const { showToast } = useToast();
  const [isExporting, setIsExporting] = useState(false);
  const [exportedText, setExportedText] = useState('');
  const [showTextPreview, setShowTextPreview] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;
    return acquireBodyScrollLock();
  }, [isOpen]);

  if (!isOpen) return null;

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const exportAsJSON = () => {
    const exportData = {
      vehicle: {
        year: vehicle.year,
        make: vehicle.make,
        model: vehicle.model,
        trim: vehicle.trim,
        color: vehicle.color,
        nickname: vehicle.nickname,
        description: vehicle.description,
        horsepower: vehicle.horsepower,
        torque: vehicle.torque,
        redline: vehicle.redline
      },
      modifications: modifications.map(mod => ({
        title: mod.title,
        description: mod.description,
        category: mod.category,
        vendor: mod.vendor,
        cost: mod.cost,
        install_date: mod.install_date,
        notes: mod.notes,
        part_number: mod.part_number,
        specifications: mod.specifications,
        parentComponent: mod.parentComponent,
        power_gain: mod.power_gain,
        torque_gain: mod.torque_gain,
        weight_change: mod.weight_change
      })),
      export_info: {
        exported_at: new Date().toISOString(),
        total_modifications: modifications.length,
        total_cost: modifications.reduce((sum, mod) => sum + (mod.cost || 0), 0)
      }
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${vehicle.display_name.replace(/\s+/g, '_')}_export.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportAsExcel = () => {
    // Create CSV data as a fallback until XLSX is properly configured
    const csvData = [];
    
    // Vehicle information
    csvData.push('Vehicle Information');
    csvData.push(`Year,${vehicle.year}`);
    csvData.push(`Make,${vehicle.make}`);
    csvData.push(`Model,${vehicle.model}`);
    csvData.push(`Trim,${vehicle.trim}`);
    csvData.push(`Exterior,${vehicle.exterior_finish_label || vehicle.color || 'N/A'}`);
    csvData.push(`Nickname,${vehicle.nickname || 'N/A'}`);
    csvData.push(
      `${vehicle.horsepower_measurement === 'wheel' ? 'Wheel horsepower (whp)' : 'Horsepower (hp)'},${vehicle.horsepower || 'N/A'}`
    );
    csvData.push(`Torque,${vehicle.torque || 'N/A'}`);
    csvData.push(`Redline,${vehicle.redline || 'N/A'}`);
    csvData.push('');
    csvData.push('Summary');
    csvData.push(`Total Modifications,${modifications.length}`);
    csvData.push(`Total Investment,${formatCurrency(modifications.reduce((sum, mod) => sum + (mod.cost || 0), 0))}`);
    csvData.push('');
    csvData.push('Modifications');
    csvData.push('Title,Category,Vendor,Cost,Install Date,Part Number,Power Gain,Torque Gain,Weight Change,Parent Component,Description,Notes');
    
    modifications.forEach(mod => {
      const row = [
        `"${mod.title}"`,
        `"${mod.category}"`,
        `"${mod.vendor}"`,
        mod.cost || 0,
        formatDate(mod.install_date),
        `"${mod.part_number || 'N/A'}"`,
        mod.power_gain || 'N/A',
        mod.torque_gain || 'N/A',
        mod.weight_change || 'N/A',
        `"${mod.parentComponent || 'N/A'}"`,
        `"${mod.description}"`,
        `"${mod.notes || 'N/A'}"`
      ].join(',');
      csvData.push(row);
    });

    const csvContent = csvData.join('\n');
    const dataBlob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${vehicle.display_name.replace(/\s+/g, '_')}_modifications.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportAsPDF = () => {
    // Create HTML content for PDF-like formatting
    let htmlContent = `
      <html>
        <head>
          <title>${escapeHtml(vehicle.display_name)} - Vehicle Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.6; }
            h1 { color: #333; border-bottom: 2px solid #333; }
            h2 { color: #555; margin-top: 30px; }
            h3 { color: #777; }
            .vehicle-info { background: #f5f5f5; padding: 15px; margin: 10px 0; }
            .modification { border: 1px solid #ddd; margin: 10px 0; padding: 15px; }
            .mod-title { font-weight: bold; font-size: 1.1em; color: #333; }
            .mod-details { margin: 10px 0; }
            .summary { background: #e8f4f8; padding: 15px; margin: 20px 0; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <h1>${escapeHtml(vehicle.display_name)}</h1>
          
          <div class="vehicle-info">
            <h2>Vehicle Information</h2>
            <p><strong>Year:</strong> ${escapeHtml(vehicle.year)}</p>
            <p><strong>Make:</strong> ${escapeHtml(vehicle.make)}</p>
            <p><strong>Model:</strong> ${escapeHtml(vehicle.model)}</p>
            <p><strong>Trim:</strong> ${escapeHtml(vehicle.trim)}</p>
            ${vehicle.exterior_finish_label || vehicle.color ? `<p><strong>Exterior:</strong> ${escapeHtml(vehicle.exterior_finish_label || vehicle.color)}</p>` : ''}
            ${vehicle.nickname ? `<p><strong>Nickname:</strong> ${escapeHtml(vehicle.nickname)}</p>` : ''}
            ${vehicle.horsepower ? `<p><strong>${vehicle.horsepower_measurement === 'wheel' ? 'Wheel horsepower (whp)' : 'Horsepower (hp)'}:</strong> ${escapeHtml(vehicle.horsepower)}</p>` : ''}
            ${vehicle.torque ? `<p><strong>Torque:</strong> ${escapeHtml(vehicle.torque)} lb-ft</p>` : ''}
            ${vehicle.redline ? `<p><strong>Redline:</strong> ${escapeHtml(vehicle.redline)} RPM</p>` : ''}
            ${vehicle.description ? `<p><strong>Description:</strong> ${escapeHtml(vehicle.description)}</p>` : ''}
          </div>
          
          <div class="summary">
            <h2>Summary</h2>
            <p><strong>Total Modifications:</strong> ${modifications.length}</p>
            <p><strong>Total Investment:</strong> ${formatCurrency(modifications.reduce((sum, mod) => sum + (mod.cost || 0), 0))}</p>
          </div>
          
          <h2>Modifications</h2>
    `;

    modifications.forEach((mod, index) => {
      htmlContent += `
        <div class="modification">
          <div class="mod-title">${index + 1}. ${escapeHtml(mod.title)}</div>
          <div class="mod-details">
            <p><strong>Category:</strong> ${escapeHtml(mod.category)}</p>
            <p><strong>Vendor:</strong> ${escapeHtml(mod.vendor)}</p>
            <p><strong>Cost:</strong> ${escapeHtml(formatCurrency(mod.cost))}</p>
            <p><strong>Install Date:</strong> ${escapeHtml(formatDate(mod.install_date))}</p>
            ${mod.part_number ? `<p><strong>Part Number:</strong> ${escapeHtml(mod.part_number)}</p>` : ''}
            ${mod.power_gain ? `<p><strong>Power Gain:</strong> ${escapeHtml(mod.power_gain)} HP</p>` : ''}
            ${mod.torque_gain ? `<p><strong>Torque Gain:</strong> ${escapeHtml(mod.torque_gain)} lb-ft</p>` : ''}
            ${mod.weight_change ? `<p><strong>Weight Change:</strong> ${escapeHtml(mod.weight_change)} lbs</p>` : ''}
            ${mod.parentComponent ? `<p><strong>Parent Component:</strong> ${escapeHtml(mod.parentComponent)}</p>` : ''}
            ${mod.description ? `<p><strong>Description:</strong> ${escapeHtml(mod.description)}</p>` : ''}
            ${mod.notes ? `<p><strong>Notes:</strong> ${escapeHtml(mod.notes)}</p>` : ''}
          </div>
        </div>
      `;
    });

    htmlContent += `
          <p style="margin-top: 40px; text-align: center; color: #666; font-size: 0.9em;">
            Exported on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}
          </p>
        </body>
      </html>
    `;

    // Create a new window and print
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    } else {
      // Fallback: download as HTML file
      const dataBlob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${vehicle.display_name.replace(/\s+/g, '_')}_modifications.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const exportAsText = () => {
    let text = `${vehicle.display_name}\n`;
    text += '='.repeat(vehicle.display_name.length) + '\n\n';
    
    text += 'VEHICLE INFORMATION\n';
    text += `Year: ${vehicle.year}\n`;
    text += `Make: ${vehicle.make}\n`;
    text += `Model: ${vehicle.model}\n`;
    text += `Trim: ${vehicle.trim}\n`;
    if (vehicle.exterior_finish_label || vehicle.color) {
      text += `Exterior: ${vehicle.exterior_finish_label || vehicle.color}\n`;
    }
    if (vehicle.nickname) text += `Nickname: ${vehicle.nickname}\n`;
    if (vehicle.horsepower) {
      text += `${vehicle.horsepower_measurement === 'wheel' ? 'Wheel horsepower (whp)' : 'Horsepower (hp)'}: ${vehicle.horsepower}\n`;
    }
    if (vehicle.torque) text += `Torque: ${vehicle.torque} lb-ft\n`;
    if (vehicle.redline) text += `Redline: ${vehicle.redline} RPM\n`;
    if (vehicle.description) text += `Description: ${vehicle.description}\n`;
    
    text += '\nSUMMARY\n';
    text += `Total Modifications: ${modifications.length}\n`;
    text += `Total Investment: ${formatCurrency(modifications.reduce((sum, mod) => sum + (mod.cost || 0), 0))}\n\n`;
    
    text += 'MODIFICATIONS\n';
    text += '-'.repeat(50) + '\n\n';
    
    modifications.forEach((mod, index) => {
      text += `${index + 1}. ${mod.title}\n`;
      text += `   Category: ${mod.category}\n`;
      text += `   Vendor: ${mod.vendor}\n`;
      text += `   Cost: ${formatCurrency(mod.cost)}\n`;
      text += `   Install Date: ${formatDate(mod.install_date)}\n`;
      if (mod.part_number) text += `   Part Number: ${mod.part_number}\n`;
      if (mod.power_gain) text += `   Power Gain: ${mod.power_gain} HP\n`;
      if (mod.torque_gain) text += `   Torque Gain: ${mod.torque_gain} lb-ft\n`;
      if (mod.weight_change) text += `   Weight Change: ${mod.weight_change} lbs\n`;
      if (mod.parentComponent) text += `   Parent Component: ${mod.parentComponent}\n`;
      if (mod.description) text += `   Description: ${mod.description}\n`;
      if (mod.notes) text += `   Notes: ${mod.notes}\n`;
      text += '\n';
    });
    
    text += `\nExported on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`;
    
    setExportedText(text);
    setShowTextPreview(true);
  };

  const handleExport = async () => {
    setIsExporting(true);
    
    try {
      switch (exportType) {
        case 'json':
          exportAsJSON();
          break;
        case 'excel':
          exportAsExcel();
          break;
        case 'pdf':
          exportAsPDF();
          break;
        case 'text':
          exportAsText();
          return; // Don't close modal for text export
      }
      
      // Close modal after successful export (except for text)
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      console.error('Export failed:', error);
      showToast('Export failed. Please try again.', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(exportedText).then(() => {
      showToast('Copied to clipboard.', 'success');
    }).catch(() => {
      showToast('Couldn’t copy. Select the text and copy manually.', 'error');
    });
  };

  const downloadTextFile = () => {
    const dataBlob = new Blob([exportedText], { type: 'text/plain' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${vehicle.display_name.replace(/\s+/g, '_')}_modifications.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getExportTitle = () => {
    switch (exportType) {
      case 'json': return 'Export as JSON';
      case 'excel': return 'Export as CSV Spreadsheet';
      case 'pdf': return 'Export as Printable Document';
      case 'text': return 'Export as Text';
      default: return 'Export Vehicle Data';
    }
  };

  const getExportDescription = () => {
    switch (exportType) {
      case 'json': return 'Export vehicle and modification data in JSON format, perfect for importing into other Bilt:Spec instances or backup purposes.';
      case 'excel': return 'Export data as a CSV file that can be opened in Excel, Google Sheets, or any spreadsheet application. Great for analysis and sharing.';
      case 'pdf': return 'Generate a formatted HTML document that opens in a new window for printing or saving as PDF. Perfect for documentation and sharing.';
      case 'text': return 'Create a formatted text version of your vehicle data that can be easily copied and pasted for sharing with friends or at car shows.';
      default: return 'Export your vehicle data in the selected format.';
    }
  };

  return (
    <div className="modal-overlay z-50 p-4">
      <div className="modal-panel max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6" role="dialog" aria-modal="true" aria-labelledby="export-modal-title">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/[0.08]">
          <h2 id="export-modal-title" className="heading-section text-xl">{getExportTitle()}</h2>
          <button
            type="button"
            onClick={onClose}
            className="btn-icon"
            aria-label="Close export"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {!showTextPreview ? (
            <>
              <div className="mb-6">
                <p className="text-gray-300 mb-4">{getExportDescription()}</p>
                
                <div className="surface rounded-lg p-4 mb-4">
                  <h3 className="text-white font-medium mb-2">Export Summary</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-400">Vehicle:</span>
                      <span className="text-white ml-2">{vehicle.display_name}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Modifications:</span>
                      <span className="text-white ml-2">{modifications.length}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Total Investment:</span>
                      <span className="text-white ml-2">{formatCurrency(modifications.reduce((sum, mod) => sum + (mod.cost || 0), 0))}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Export Date:</span>
                      <span className="text-white ml-2">{new Date().toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                  disabled={isExporting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleExport}
                  disabled={isExporting}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white rounded-lg transition-colors flex items-center space-x-2"
                >
                  {isExporting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Exporting...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-4-4m4 4l4-4m-4-4V3" />
                      </svg>
                      <span>Export</span>
                    </>
                  )}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="mb-4">
                <h3 className="text-white font-medium mb-2">Text Export Preview</h3>
                <p className="text-gray-400 text-sm mb-4">
                  You can copy this text to share with friends or save it as a file.
                </p>
              </div>

              <div className="bg-[#0a0a0a] rounded-lg p-4 mb-4 max-h-96 overflow-y-auto">
                <pre className="text-gray-300 text-sm whitespace-pre-wrap font-mono">
                  {exportedText}
                </pre>
              </div>

              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowTextPreview(false)}
                  className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={downloadTextFile}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-4-4m4 4l4-4m-4-4V3" />
                  </svg>
                  <span>Download File</span>
                </button>
                <button
                  onClick={copyToClipboard}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span>Copy to Clipboard</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
